/**
 * Secure Token Handler
 * 
 * Stores access tokens and refresh tokens in memory and cookies for persistence.
 * Uses secure cookie attributes (SameSite, Secure) for better security.
 * This handler is framework-agnostic and safe to import across multiple modules.
 * 
 * Note: For maximum security, consider using httpOnly cookies set by the backend.
 * This implementation uses regular cookies accessible to JavaScript.
 */

// Cookie names
const ACCESS_TOKEN_KEY = 'pp_access_token';
const REFRESH_TOKEN_KEY = 'pp_refresh_token';

// Cookie options - using secure attributes
const getCookieOptions = (): string => {
  const isSecure = window.location.protocol === 'https:';
  const sameSite = 'Strict'; // Can be 'Lax' or 'Strict'
  
  let options = `SameSite=${sameSite}; Path=/`;
  
  // Only set Secure flag in HTTPS
  if (isSecure) {
    options += '; Secure';
  }
  
  return options;
};

// Helper functions for cookie operations
const setCookie = (name: string, value: string, days?: number): void => {
  try {
    let cookieString = `${name}=${value}; ${getCookieOptions()}`;
    
    // Set expiration if days provided, otherwise it's a session cookie
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieString += `; Expires=${date.toUTCString()}`;
    }
    
    document.cookie = cookieString;
  } catch (e) {
    console.warn(`Failed to set cookie ${name}:`, e);
  }
};

const getCookie = (name: string): string | null => {
  try {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  } catch (e) {
    console.warn(`Failed to get cookie ${name}:`, e);
    return null;
  }
};

const deleteCookie = (name: string): void => {
  try {
    document.cookie = `${name}=; ${getCookieOptions()}; Expires=Thu, 01 Jan 1970 00:00:00 UTC`;
  } catch (e) {
    console.warn(`Failed to delete cookie ${name}:`, e);
  }
};

// Private in-memory storage for tokens
let accessToken: string | null = null;
let refreshToken: string | null = null;

// Initialize: restore tokens from cookies on module load
(function initialize() {
  try {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const storedAccessToken = getCookie(ACCESS_TOKEN_KEY);
      const storedRefreshToken = getCookie(REFRESH_TOKEN_KEY);
      
      if (storedAccessToken) {
        accessToken = storedAccessToken;
      }
      if (storedRefreshToken) {
        refreshToken = storedRefreshToken;
      }
    }
  } catch (e) {
    // Cookies might not be available (e.g., in SSR)
    console.warn('Failed to initialize tokens from cookies:', e);
  }
})();

/**
 * Sets the access token in memory and cookies.
 * @param token - The access token to store
 */
export function setAccessToken(token: string): void {
  accessToken = token;
  // Store as session cookie (expires when browser closes)
  setCookie(ACCESS_TOKEN_KEY, encodeURIComponent(token));
}

/**
 * Retrieves the access token from memory.
 * @returns The access token if set, null otherwise
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Clears the access token from memory and cookies.
 */
export function clearAccessToken(): void {
  accessToken = null;
  deleteCookie(ACCESS_TOKEN_KEY);
}

/**
 * Sets the refresh token in memory and cookies.
 * @param token - The refresh token to store
 */
export function setRefreshToken(token: string): void {
  refreshToken = token;
  // Store as session cookie (expires when browser closes)
  setCookie(REFRESH_TOKEN_KEY, encodeURIComponent(token));
}

/**
 * Retrieves the refresh token from memory.
 * @returns The refresh token if set, null otherwise
 */
export function getRefreshToken(): string | null {
  return refreshToken;
}

/**
 * Clears the refresh token from memory and cookies.
 */
export function clearRefreshToken(): void {
  refreshToken = null;
  deleteCookie(REFRESH_TOKEN_KEY);
}

/**
 * Clears all tokens (both access and refresh) from memory and cookies.
 */
export function clearAllTokens(): void {
  accessToken = null;
  refreshToken = null;
  deleteCookie(ACCESS_TOKEN_KEY);
  deleteCookie(REFRESH_TOKEN_KEY);
}
